import { useState, useEffect } from 'react';
import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc, orderBy, query } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../lib/firebase';
import { TeamMember, TrainingCamp } from '../lib/types';
import { AddMemberForm } from '../components/TeamAdmin/AddMemberForm';
import { TeamMembersSection } from '../components/TeamAdmin/TeamMembersSection';
import { TrainingCampsSection } from '../components/TeamAdmin/TrainingCampsSection';
import { LogOut } from 'lucide-react';
import { logout } from '../lib/auth';
import { useNavigate } from 'react-router-dom';

export default function TeamAdmin() {
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [camps, setCamps] = useState<TrainingCamp[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    const [membersSnapshot, campsSnapshot] = await Promise.all([
      getDocs(query(collection(db, 'team'), orderBy('order'))),
      getDocs(query(collection(db, 'trainingCamps'), orderBy('order')))
    ]);

    const loadedMembers = membersSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as TeamMember));

    const loadedCamps = campsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as TrainingCamp));

    setMembers(loadedMembers);
    setCamps(loadedCamps);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const handleImageUpload = async (file: File) => {
    const storageRef = ref(storage, `team/${file.name}`);
    await uploadBytes(storageRef, file);
    return getDownloadURL(storageRef);
  };

  const handleAddMember = async (memberData: Partial<TeamMember>, file: File) => {
    try {
      const imageUrl = await handleImageUpload(file);
      await addDoc(collection(db, 'team'), {
        ...memberData,
        imageUrl,
        order: members.length,
      });
      loadData();
    } catch (error) {
      console.error('Error adding team member:', error);
    }
  };

  const handleMemberOrderChange = async (memberId: string, newOrder: number) => {
    const member = members.find(m => m.id === memberId);
    if (!member) return;

    const boundedOrder = Math.max(0, Math.min(newOrder, members.length - 1));
    const updatedMembers = members.filter(m => m.id !== memberId);
    updatedMembers.splice(boundedOrder, 0, member);
    
    const reorderedMembers = updatedMembers.map((m, index) => ({
      ...m,
      order: index
    }));

    setMembers(reorderedMembers);

    for (const m of reorderedMembers) {
      const memberRef = doc(db, 'team', m.id);
      await updateDoc(memberRef, { order: m.order });
    }
  };

  const handleMemberDragEnd = async (result: any) => {
    if (!result.destination) return;

    const items = Array.from(members);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setMembers(items);

    for (let i = 0; i < items.length; i++) {
      const member = items[i];
      const memberRef = doc(db, 'team', member.id);
      await updateDoc(memberRef, { order: i });
    }
  };

  const handleDeleteMember = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this team member?')) {
      await deleteDoc(doc(db, 'team', id));
      loadData();
    }
  };

  const handleEditMember = async (editedMember: TeamMember, file: File | null) => {
    try {
      let imageUrl = editedMember.imageUrl;
      if (file) {
        imageUrl = await handleImageUpload(file);
      }

      const memberRef = doc(db, 'team', editedMember.id);
      await updateDoc(memberRef, {
        ...editedMember,
        imageUrl,
      });

      loadData();
    } catch (error) {
      console.error('Error updating team member:', error);
    }
  };

  const handleCampSave = async (campData: Partial<TrainingCamp>, file: File | null) => {
    try {
      let imageUrl = (campData as TrainingCamp).imageUrl;
      if (file) {
        const storageRef = ref(storage, `camps/${file.name}`);
        await uploadBytes(storageRef, file);
        imageUrl = await getDownloadURL(storageRef);
      }

      if ((campData as TrainingCamp).id) {
        const campRef = doc(db, 'trainingCamps', (campData as TrainingCamp).id);
        await updateDoc(campRef, {
          ...campData,
          imageUrl,
        });
      } else {
        await addDoc(collection(db, 'trainingCamps'), {
          ...campData,
          imageUrl,
          order: camps.length,
        });
      }

      loadData();
    } catch (error) {
      console.error('Error saving training camp:', error);
    }
  };

  const handleDeleteCamp = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this training camp?')) {
      await deleteDoc(doc(db, 'trainingCamps', id));
      loadData();
    }
  };

  const handleCampOrderChange = async (campId: string, newOrder: number) => {
    const camp = camps.find(c => c.id === campId);
    if (!camp) return;

    const boundedOrder = Math.max(0, Math.min(newOrder, camps.length - 1));
    const updatedCamps = camps.filter(c => c.id !== campId);
    updatedCamps.splice(boundedOrder, 0, camp);
    
    const reorderedCamps = updatedCamps.map((c, index) => ({
      ...c,
      order: index
    }));

    setCamps(reorderedCamps);

    for (const c of reorderedCamps) {
      const campRef = doc(db, 'trainingCamps', c.id);
      await updateDoc(campRef, { order: c.order });
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold">Team Management</h1>
        <button
          onClick={handleLogout}
          className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded-lg text-gray-700 transition-colors"
        >
          <LogOut className="w-5 h-5" />
          Logout
        </button>
      </div>

      <AddMemberForm onSubmit={handleAddMember} />

      <TeamMembersSection
        members={members}
        onOrderChange={handleMemberOrderChange}
        onDelete={handleDeleteMember}
        onSave={handleEditMember}
        onDragEnd={handleMemberDragEnd}
      />

      <TrainingCampsSection
        camps={camps}
        onOrderChange={handleCampOrderChange}
        onDelete={handleDeleteCamp}
        onSave={handleCampSave}
      />
    </div>
  );
}